import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, AlertCircle, Calendar, FileText, Tag, Hash, Brain, ChevronDown, ChevronUp, Clock,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useTranscript } from '../hooks/useTranscript';
import { chipStyle } from './reportHelpers';

/**
 * Route component for /transcripts/:transcriptId. Fetches the full
 * Transcript doc (chunks + raw text) and renders it as a multi-section
 * page. Companion to TranscriptUpload's list view.
 *
 * Sections:
 *   - Hero (title, uploadedAt, stats)
 *   - Ticker / theme chips
 *   - Analysis summary + key points
 *   - Per-chunk breakdown (each chunk's text + per-chunk summary)
 *   - Raw transcript (collapsed by default; the full original text)
 */
const TranscriptDetailRoute = () => {
  const { transcriptId } = useParams();
  const navigate = useNavigate();
  const { transcript, loading, error } = useTranscript(transcriptId);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error === 'not-found') {
    return (
      <BackShell onBack={() => navigate('/transcripts')}>
        <div className="empty-state">
          <AlertCircle size={48} />
          <p>Transcript not found.</p>
          <p style={{ fontSize: '0.875rem' }}>It may have been deleted, or the link may be stale.</p>
        </div>
      </BackShell>
    );
  }

  if (error) {
    return (
      <BackShell onBack={() => navigate('/transcripts')}>
        <div className="error">
          <AlertCircle size={24} />
          <p>{error}</p>
        </div>
      </BackShell>
    );
  }

  if (!transcript) return null;

  return <TranscriptDetail transcript={transcript} onBack={() => navigate('/transcripts')} />;
};

const BackShell = ({ onBack, children }) => (
  <div>
    <button
      className="btn btn-secondary"
      onClick={onBack}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginBottom: '1rem' }}
    >
      <ArrowLeft size={16} /> Back to transcripts
    </button>
    {children}
  </div>
);

const TranscriptDetail = ({ transcript, onBack }) => {
  const date = transcript.uploadedAt ? parseISO(transcript.uploadedAt) : null;
  const analyzedAt = transcript.analysis?.analyzedAt ? parseISO(transcript.analysis.analyzedAt) : null;
  const chunks = transcript.chunks || [];
  const tickers = transcript.extractedTickers || [];
  const themes = transcript.extractedThemes || [];
  const summary = transcript.analysis?.summary || '';
  const keyPoints = transcript.analysis?.keyPoints || [];
  const isAnalyzing = !analyzedAt && chunks.length === 0;

  return (
    <div>
      <BackShell onBack={onBack} />

      {/* Hero */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          <FileText size={16} />
          <span>Transcript</span>
        </div>
        <h1 style={{ margin: 0, marginBottom: '0.5rem', lineHeight: 1.3 }}>
          {transcript.title || '(untitled)'}
        </h1>

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '1.25rem',
          fontSize: '0.875rem', color: 'var(--text-secondary)',
          paddingTop: '0.5rem',
        }}>
          {date && <Stat icon={<Calendar size={14} />} label="Uploaded" value={format(date, 'MMM dd, yyyy')} />}
          <Stat icon={<Hash size={14} />} label="Chunks" value={chunks.length} />
          {analyzedAt && <Stat icon={<Clock size={14} />} label="Analyzed" value={format(analyzedAt, 'MMM dd, h:mm a')} />}
          {transcript.contentType && <Stat label="Type" value={transcript.contentType} />}
          {transcript.triggeredPhilosophyUpdate && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--primary-color)' }}>
              <Brain size={14} /> Triggered philosophy update
            </span>
          )}
        </div>

        {isAnalyzing && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            background: 'var(--severity-warn-bg)',
            borderLeft: '4px solid var(--severity-warn-border)',
            borderRadius: '4px',
            fontSize: '0.9375rem',
          }}>
            <strong>Still analyzing...</strong> The orchestrator runs ~30-90s after upload.
            Reload the page once it's done to see chunks, summary, and extractions.
          </div>
        )}
      </div>

      {/* Ticker + theme chips */}
      {(tickers.length > 0 || themes.length > 0) && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <SectionHeader icon={<Tag size={16} />}>Extracted</SectionHeader>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {tickers.map((t) => (
              <span key={`tk-${t}`} style={chipStyle('blue')}>{t}</span>
            ))}
            {themes.map((th) => (
              <span key={`th-${th}`} style={chipStyle('purple')}>{th}</span>
            ))}
          </div>
        </div>
      )}

      {/* Analysis summary + key points */}
      {(summary || keyPoints.length > 0) && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <SectionHeader>Analysis</SectionHeader>
          {summary && (
            <p style={{ margin: '0 0 1rem', fontSize: '1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {summary}
            </p>
          )}
          {keyPoints.length > 0 && (
            <>
              <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Key points
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {keyPoints.map((kp, i) => (
                  <li key={i} style={{ fontSize: '0.9375rem', lineHeight: 1.55 }}>{kp}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Chunks */}
      {chunks.length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <SectionHeader>Chunks ({chunks.length})</SectionHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {chunks.map((c) => (
              <ChunkCard key={c.index} chunk={c} />
            ))}
          </div>
        </div>
      )}

      {/* Raw transcript -- collapsed by default */}
      <RawTranscriptSection
        rawText={transcript.rawTranscript}
        normalizedText={transcript.normalizedTranscript}
      />
    </div>
  );
};

// ---------------------------------------------------------------------

const ChunkCard = ({ chunk }) => {
  const [expanded, setExpanded] = useState(false);
  const text = chunk.text || '';
  const summary = chunk.summary || '';
  const preview = text.length > 240 ? text.slice(0, 240) + '...' : text;

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      padding: '0.875rem',
      background: 'var(--bg-primary)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '0.5rem', marginBottom: '0.5rem',
      }}>
        <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
          Chunk {chunk.index + 1}
          {chunk.embeddingId && (
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: '0.5rem', fontSize: '0.75rem' }}>
              embedded
            </span>
          )}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => setExpanded(!expanded)}
          style={{ padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {summary && (
        <p style={{
          margin: '0 0 0.5rem',
          fontSize: '0.9375rem',
          lineHeight: 1.55,
          color: 'var(--text-primary)',
          fontStyle: 'italic',
        }}>
          {summary}
        </p>
      )}

      <div style={{
        fontSize: '0.8125rem',
        color: 'var(--text-secondary)',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      }}>
        {expanded ? text : preview}
      </div>
    </div>
  );
};

const RawTranscriptSection = ({ rawText, normalizedText }) => {
  const [view, setView] = useState('hidden'); // 'hidden' | 'raw' | 'normalized'
  if (!rawText && !normalizedText) return null;

  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
        <SectionHeader>Source text</SectionHeader>
        <div className="filter-buttons" style={{ margin: 0 }}>
          <button
            className={`btn ${view === 'hidden' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setView('hidden')}
            style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
          >
            Hide
          </button>
          {normalizedText && (
            <button
              className={`btn ${view === 'normalized' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView('normalized')}
              style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
            >
              Normalized
            </button>
          )}
          {rawText && (
            <button
              className={`btn ${view === 'raw' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView('raw')}
              style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
            >
              Raw
            </button>
          )}
        </div>
      </div>

      {view !== 'hidden' && (
        <pre style={{
          margin: 0,
          marginTop: '0.5rem',
          padding: '0.875rem',
          background: 'var(--bg-secondary, #f8f9fa)',
          borderRadius: '6px',
          fontSize: '0.8125rem',
          lineHeight: 1.5,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          whiteSpace: 'pre-wrap',
          maxHeight: '600px',
          overflow: 'auto',
          color: 'var(--text-secondary)',
        }}>
          {view === 'normalized' ? normalizedText : rawText}
        </pre>
      )}
    </div>
  );
};

const SectionHeader = ({ icon, children }) => (
  <h2 style={{
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: 0,
    marginBottom: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  }}>
    {icon}
    {children}
  </h2>
);

const Stat = ({ icon, label, value }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
    {icon}
    <span style={{ color: 'var(--text-secondary)' }}>{label}:</span>
    <strong style={{ color: 'var(--text-primary)' }}>{value}</strong>
  </div>
);

export default TranscriptDetailRoute;
